import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Download, RefreshCw, Building2, CalendarClock, History, Hash, ChevronDown } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useCurrentDomain } from '../../context/DomainContext';
import { getDMEDataEntryReport } from '../../../src/services/masterDataService';
import moment from 'moment';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './DMEDataEntryReport.css';
import { predefinedBottomRanges, selectStyles } from '../../helpers';
import { THEME } from '../../theme/themeColors';
import { useThemeHex } from '../../theme/useThemeHex';

const TREND_WEEKS_BACK = 5;

const COLUMN_DIVIDER = '1px solid #e5e7eb';

const CHART_MOBILE_BREAKPOINT = 950;

const COLUMNS = [
    { label: 'Creation Source', key: 'caseCreationSource' },  // 0
    { label: 'Comp Name',    key: 'companyName' },            // 1
    { label: 'Case No',         key: 'caseNo' },              // 2
    { label: 'Patient Name',    key: 'patientName' },         // 3
    { label: 'Created Date',    key: 'createdDate' },         // 4
    { label: 'Updated Date',    key: 'updatedDate' },         // 5
    { label: 'Created By',      key: 'createdByUser' },       // 6
    { label: 'Updated By',      key: 'updatedByUser' },       // 7
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const pageSizeSelectOptions = PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }));

const DMEDataEntryReport = () => {
    const { domain } = useCurrentDomain();
    const chartPrimary = useThemeHex('--theme-primary');

    const [records, setRecords]                 = useState([]);
    const [loading, setLoading]           = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [totalCount, setTotalCount]     = useState(0);
    const [pageNumber, setPageNumber]     = useState(1);
    const [pageSize, setPageSize]         = useState(50);    
    const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 35); return d; });
    const [toDate, setToDate]     = useState(new Date());
    const [isExcelHovered, setIsExcelHovered] = useState(false);    
    const [colWidths, setColWidths] = useState([120, 190, 110, 180, 140, 150, 150, 140, 140, 120]);


    const [viewMode, setViewMode] = useState('summary');
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [weekBuckets, setWeekBuckets] = useState([]);
    const [pivotRows, setPivotRows] = useState([]);
    const [expandedCompanies, setExpandedCompanies] = useState(new Set());

    const [trendLoading, setTrendLoading] = useState(false);
    const [trendData, setTrendData] = useState([]);

    const activeIndex = useRef(null);
    const startX      = useRef(0);
    const startWidth  = useRef(0);
    const isFirstDateEffect = useRef(true);

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < CHART_MOBILE_BREAKPOINT);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleMouseDown = (index, e) => {
        activeIndex.current = index;
        startX.current      = e.clientX;
        startWidth.current  = colWidths[index];
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        if (activeIndex.current === null) return;
        const computedWidth = Math.max(60, startWidth.current + (e.clientX - startX.current));
        setColWidths((prev) => {
            const updated = [...prev];
            updated[activeIndex.current] = computedWidth;
            return updated;
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        activeIndex.current = null;
        document.body.style.userSelect = '';
    }, [handleMouseMove]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    const fetchRecords = useCallback(async (page, size, isNewSearch) => {
        const setLoader = isNewSearch ? setSearchLoading : setLoading;
        setLoader(true);
        try {
            const payload = {
                domain: domain,
                pageNumber: page,
                pageSize: size,
                fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : '',
                toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : '',
            }
            const res = await getDMEDataEntryReport(payload);
            setRecords(res.details || []);
            setTotalCount(res.totalCount || 0);
            setPageNumber(res.pageNumber || page);
            setPageSize(res.pageSize || size);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load report data');
            console.error(err);
        } finally {
            setLoader(false);
        }
    }, [domain, fromDate, toDate]);

    const buildWeeklyBuckets = (rangeFrom, rangeTo) => {
        if (!rangeFrom || !rangeTo) return [];
        const today = moment().endOf('day');
        const effectiveTo = moment(rangeTo).isAfter(today) ? today : moment(rangeTo);
        if (moment(rangeFrom).startOf('day').isAfter(effectiveTo)) return [];

        const weeks = [];
        let cursor = moment(effectiveTo).startOf('week');
        const firstWeekStart = moment(rangeFrom).startOf('week');
        const clampFrom = moment(rangeFrom).startOf('day');
        let safety = 0;
        while (cursor.isSameOrAfter(firstWeekStart) && safety < 3000) {
            const start = cursor.clone();
            const end = cursor.clone().endOf('week');

            const displayStart = start.isBefore(clampFrom) ? clampFrom.clone() : start;
            const displayEnd = end.isAfter(effectiveTo) ? effectiveTo.clone() : end;
            weeks.push({
                start,
                end,
                key: start.format('YYYY-MM-DD'),
                label: `${displayEnd.format('MM/DD/YY')} - ${displayStart.format('MM/DD/YY')}`,
                shortLabel: start.format('MM/DD'),
            });
            cursor = cursor.clone().subtract(7, 'days');
            safety += 1;
        }
        return weeks;
    };

    const buildPivotData = (allRecords, weeks) => {
        const companyMap = new Map();
        (allRecords || []).forEach((row) => {
            const company = row.companyName || 'Unknown';
            const isAI = (row.caseCreationSource || '').toUpperCase() === 'AI MODULE';

            if (!companyMap.has(company)) {
                const weekly = {};
                weeks.forEach((w) => { weekly[w.label] = { ai: 0, manual: 0 }; });
                companyMap.set(company, { companyName: company, total: { ai: 0, manual: 0 }, weekly });
            }
            const entry = companyMap.get(company);
            if (isAI) entry.total.ai += 1; else entry.total.manual += 1;

            const createdMoment = row.createdDate ? moment(row.createdDate) : null;
            if (createdMoment) {
                const week = weeks.find((w) => createdMoment.isBetween(w.start, w.end, null, '[]'));
                if (week) {
                    if (isAI) entry.weekly[week.label].ai += 1;
                    else entry.weekly[week.label].manual += 1;
                }
            }
        });
        return Array.from(companyMap.values()).sort((a, b) => a.companyName.localeCompare(b.companyName));
    };

    const fetchSummaryData = useCallback(async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        setSummaryLoading(true);
        try {
            const payload = {
                domain: domain,
                fromDate: moment(fromDate).format('YYYY-MM-DD'),
                toDate: moment(toDate).format('YYYY-MM-DD'),
                isExport: true,
            };
            const res = await getDMEDataEntryReport(payload);
            const allRecords = res.details || [];
            const weeks = buildWeeklyBuckets(fromDate, toDate);
            setWeekBuckets(weeks);
            setPivotRows(buildPivotData(allRecords, weeks));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load summary data');
            console.error(err);
        } finally {
            setSummaryLoading(false);
        }
    }, [domain, fromDate, toDate]);

const buildTrendWeeklyBuckets = (rangeFrom, rangeTo, maxWeeks = TREND_WEEKS_BACK) => {
    const allWeeks = buildWeeklyBuckets(rangeFrom, rangeTo);
    return allWeeks.slice(0, maxWeeks);
};

    const buildTrendWeeklyData = (allRecords, weeks) => {
        const byKey = new Map(weeks.map((w) => [w.key, { label: w.label, shortLabel: w.shortLabel, ai: 0, manual: 0 }]));
        (allRecords || []).forEach((row) => {
            if (!row.createdDate) return;
            const createdMoment = moment(row.createdDate);
            const week = weeks.find((w) => createdMoment.isBetween(w.start, w.end, null, '[]'));
            if (!week) return;
            const bucket = byKey.get(week.key);
            const isAI = (row.caseCreationSource || '').toUpperCase() === 'AI MODULE';
            if (isAI) bucket.ai += 1; else bucket.manual += 1;
        });
        return weeks.map((w) => byKey.get(w.key));
    };

const fetchWeeklyTrend = useCallback(async () => {
    if (!fromDate || !toDate) {
        setTrendData([]);
        return;
    }
    setTrendLoading(true);
    try {
        const weeks = buildTrendWeeklyBuckets(fromDate, toDate);
        if (weeks.length === 0) {
            setTrendData([]);
            return;
        }
        const earliestWeek = weeks[weeks.length - 1];
        const payload = {
            domain: domain,
            fromDate: earliestWeek.start.format('YYYY-MM-DD'),
            toDate: moment(toDate).format('YYYY-MM-DD'),
            isExport: true,
        };
        const res = await getDMEDataEntryReport(payload);
        setTrendData(buildTrendWeeklyData(res.details || [], weeks));
    } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load trend data');
        console.error(err);
    } finally {
        setTrendLoading(false);
    }
}, [domain, fromDate, toDate]);

    useEffect(() => {
        if (!domain) return;
        if (viewMode === 'summary') fetchSummaryData();
        else fetchRecords(1, pageSize, true);
    }, [domain]);

    useEffect(() => {
        if (!domain) return;
        fetchWeeklyTrend();
    }, [domain, fetchWeeklyTrend]);


    useEffect(() => {
        if (isFirstDateEffect.current) {
            isFirstDateEffect.current = false;
            return;
        }
        if (!fromDate || !toDate) return;
        if (viewMode === 'summary') fetchSummaryData();
        else fetchRecords(1, pageSize, true);
    }, [fromDate, toDate]);

    const handleSearch = () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        if (viewMode === 'summary') fetchSummaryData();
        else fetchRecords(1, pageSize, true);
    };

    const handleViewModeChange = (mode) => {
        if (mode === viewMode) return;
        setViewMode(mode);
        if (!fromDate || !toDate) return;
        if (mode === 'summary') fetchSummaryData();
        else fetchRecords(pageNumber, pageSize, false);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    const goToPage = (page) => {
        if (page < 1 || page > totalPages || loading) return;
        fetchRecords(page, pageSize, false);
    };

    const handlePageSizeChange = (opt) => {
        fetchRecords(1, opt.value, false);
    };

    const grandTotal = pivotRows.reduce((acc, row) => {
        acc.total.ai += row.total.ai;
        acc.total.manual += row.total.manual;
        weekBuckets.forEach((w) => {
            if (!acc.weekly[w.label]) acc.weekly[w.label] = { ai: 0, manual: 0 };
            acc.weekly[w.label].ai += row.weekly[w.label]?.ai || 0;
            acc.weekly[w.label].manual += row.weekly[w.label]?.manual || 0;
        });
        return acc;
    }, { total: { ai: 0, manual: 0 }, weekly: {} });

    const exportDetailedToExcel = async () => {
        const tid = toast.loading('Preparing export...');
        try {
            const payload = {
                domain: domain,
                fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : '',
                toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : '',
                isExport: true,
            }

            const data = await getDMEDataEntryReport(payload);

            if (!data?.details?.length) { toast.error('No data to export'); return; }

            const exportData = data?.details?.map(row => ({
                CREATION_SOURCE: row.caseCreationSource || '',
                COMPANY_NAME:    row.companyName || '',
                CASE_NO:         row.caseNo || '',
                PATIENT_NAME:    `${row.firstName || ''} ${row.lastName || ''}`.trim(),
                CREATED_DATE:    row.createdDate ? new Date(row.createdDate).toLocaleString() : '',
                UPDATED_DATE:    row.updatedDate ? new Date(row.updatedDate).toLocaleString() : '',
                CREATED_BY:      row.createdByUser || '',
                UPDATED_BY:      row.updatedByUser || '',
            }));
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'DMEDataEntry');
            XLSX.writeFile(wb, `DMEDataEntryReport_Detailed_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${exportData.length} record${exportData.length !== 1 ? 's' : ''}`);
        } catch {
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const exportSummaryToExcel = () => {
        if (!pivotRows.length) { toast.error('No data to export'); return; }
        const tid = toast.loading('Preparing export...');
        try {
            const numWeekCols = weekBuckets.length * 2;
            const totalCols = 4 + numWeekCols;

            const headerRow1 = new Array(totalCols).fill('');
            headerRow1[0] = 'Sr.no';
            headerRow1[1] = 'Company Name';
            headerRow1[2] = 'Total';
            weekBuckets.forEach((w, i) => { headerRow1[4 + i * 2] = w.label; });

            const headerRow2 = new Array(totalCols).fill('');
            headerRow2[2] = 'AI Module';
            headerRow2[3] = 'Manual';
            weekBuckets.forEach((w, i) => {
                headerRow2[4 + i * 2] = 'AI Module';
                headerRow2[5 + i * 2] = 'Manual';
            });

            const dataRows = pivotRows.map((row, idx) => {
                const r = [idx + 1, row.companyName, row.total.ai, row.total.manual];
                weekBuckets.forEach((w) => {
                    r.push(row.weekly[w.label]?.ai || 0);
                    r.push(row.weekly[w.label]?.manual || 0);
                });
                return r;
            });

            const grandTotalRow = ['Grand Total', '', grandTotal.total.ai, grandTotal.total.manual];
            weekBuckets.forEach((w) => {
                grandTotalRow.push(grandTotal.weekly[w.label]?.ai || 0);
                grandTotalRow.push(grandTotal.weekly[w.label]?.manual || 0);
            });

            const aoa = [headerRow1, headerRow2, ...dataRows, grandTotalRow];
            const ws = XLSX.utils.aoa_to_sheet(aoa);

            const lastRowIdx = aoa.length - 1;
            const merges = [
                { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
                { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
                { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } },
                { s: { r: lastRowIdx, c: 0 }, e: { r: lastRowIdx, c: 1 } },
            ];
            weekBuckets.forEach((_, i) => {
                merges.push({ s: { r: 0, c: 4 + i * 2 }, e: { r: 0, c: 5 + i * 2 } });
            });
            ws['!merges'] = merges;

            ws['!cols'] = [
                { wch: 6 }, { wch: 28 }, { wch: 10 }, { wch: 10 },
                ...weekBuckets.flatMap(() => [{ wch: 11 }, { wch: 11 }]),
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'WeeklySummary');
            XLSX.writeFile(wb, `DMEDataEntryReport_WeeklySummary_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${pivotRows.length} compan${pivotRows.length !== 1 ? 'ies' : 'y'}`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const exportToExcel = async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        if (viewMode === 'summary') {
            exportSummaryToExcel();
        } else {
            await exportDetailedToExcel();
        }
    };

    const getSourceBadgeStyles = (src) => {
        switch ((src || '').toUpperCase()) {
            case 'AI MODULE': return { backgroundColor: THEME.alpha(0.12), color: THEME.primaryHover };
            case 'MANUAL':    return { backgroundColor: '#f1f5f9', color: '#475569' };
            default:          return { backgroundColor: '#f1f5f9', color: '#475569' };
        }
    };

    const getInitials = (first, last) => {
        const a = (first || '').trim().charAt(0);
        const b = (last || '').trim().charAt(0);
        const initials = `${a}${b}`.toUpperCase();
        return initials || '—';
    };

    const toggleCompanyExpand = (companyName) => {
        setExpandedCompanies((prev) => {
            const next = new Set(prev);
            if (next.has(companyName)) next.delete(companyName);
            else next.add(companyName);
            return next;
        });
    };

    const filterLabelStyle = { fontSize: '10px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px', display: 'block' };

    const tdStyle = (i, isLast = false) => ({
        width: `${colWidths[i]}px`,
        maxWidth: `${colWidths[i]}px`,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        borderRight: isLast ? 'none' : COLUMN_DIVIDER,
        verticalAlign: 'top',
    });

    const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
    const rangeEnd   = Math.min(pageNumber * pageSize, totalCount);

    const renderCount = (value) => (
        value === 0 ? <span className="dme-zero-value">0</span> : value
    );

    return (
        <div className="bg-light min-vh-100 dme-page-wrap">
            <Toaster position="top-right" />

            <div className="card shadow-sm border-0 rounded-3 bg-white dme-card">

                <div className="border-bottom px-2 py-2" style={{ backgroundColor: '#fcfcfc' }}>
                    <div className="d-flex gap-2 align-items-end justify-content-between dme-toolbar-row">

                        <div className="dme-date-filter">
                            <label style={filterLabelStyle}>Created Date</label>
                            <DateRangePicker
                                value={[fromDate, toDate]}
                                onChange={(value) => {
                                    if (!value || !Array.isArray(value) || value.length !== 2) {
                                        setFromDate(null);
                                        setToDate(null);
                                        return;
                                    }
                                    const [start, end] = value;
                                    setFromDate(start || null);
                                    setToDate(end || null);
                                }}
                                ranges={predefinedBottomRanges}
                                placeholder="Select Date Range"
                                style={{ width: '100%' }}
                                format="MM/dd/yyyy"
                                size="sm"
                                cleanable
                                placement="bottomStart"
                                preventOverflow
                                showOneCalendar={isMobile}
                                popperClassName="dme-datepicker-popper"
                            />
                            </div>

                        <div className="d-flex gap-2 dme-toolbar-actions">
                            <div className="btn-group dme-view-toggle" role="group" style={{ height: '32px' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                        fontSize: '12px', height: '32px',
                                        backgroundColor: viewMode === 'summary' ? THEME.primary : '#fff',
                                        color: viewMode === 'summary' ? THEME.onPrimary : '#333',
                                        border: '1px solid #dee2e6',
                                    }}
                                    onClick={() => handleViewModeChange('summary')}
                                    title="Weekly summary view"
                                >Weekly Summary</button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                        fontSize: '12px', height: '32px',
                                        backgroundColor: viewMode === 'detailed' ? THEME.primary : '#fff',
                                        color: viewMode === 'detailed' ? THEME.onPrimary : '#333',
                                        border: '1px solid #dee2e6', borderLeft: 'none',
                                    }}
                                    onClick={() => handleViewModeChange('detailed')}
                                    title="Detailed view"
                                >Detailed View</button>
                            </div>
                            <button
                                className="btn fw-medium d-flex align-items-center justify-content-center gap-1 px-3 dme-toolbar-btn"
                                style={{ backgroundColor: THEME.primary, color: THEME.onPrimary, height: '32px', fontSize: '12px' }}
                                onClick={handleSearch} disabled={loading || searchLoading} title="Search"
                            >
                                {searchLoading
                                    ? <RefreshCw size={13} style={{ animation: 'dme-spin 1s linear infinite', color: THEME.onPrimary }} />
                                    : <Search size={13} style={{ color: THEME.onPrimary }} />}
                                <span className="dme-btn-label">Search</span>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 px-3 dme-toolbar-btn"
                                style={{ height: '32px', fontSize: '12px', backgroundColor: isExcelHovered ? '#f8f9fa' : '#fff', color: '#333' }}
                                onMouseEnter={() => setIsExcelHovered(true)}
                                onMouseLeave={() => setIsExcelHovered(false)}
                                onClick={exportToExcel} title="Export to Excel"
                            >
                                <Download size={13} style={{ color: '#6c757d' }} />
                                <span className="dme-btn-label">Export</span>
                            </button>
                        </div>

                    </div>
                </div>

                {viewMode === 'detailed' && (
                <div className="table-responsive dme-desktop-table" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <table
                        className="table table-hover align-middle mb-0 dme-detailed-table"
                        style={{ tableLayout: 'fixed', fontSize: '12.5px', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
                    >
                        <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                            <tr>
                                {COLUMNS.map((col, index) => (
                                    <th
                                        key={index}
                                        className="text-secondary fw-bold text-uppercase py-1 px-2 position-relative"
                                        style={{
                                            width: `${colWidths[index]}px`,
                                            minWidth: `${colWidths[index]}px`,
                                            maxWidth: `${colWidths[index]}px`,
                                            fontSize: '10.5px',
                                            letterSpacing: '0.4px',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            borderRight: index < COLUMNS.length - 1 ? COLUMN_DIVIDER : 'none',
                                        }}
                                    >
                                        <span className="d-flex align-items-center gap-1">
                                            {col.label}
                                            {index === 6 && (
                                                <span style={{ fontSize: '9px', color: THEME.primaryHover, fontWeight: 700 }}>↓</span>
                                            )}
                                        </span>
                                        <div
                                            onMouseDown={(e) => handleMouseDown(index, e)}
                                            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 11 }}
                                            className="dme-column-resizer-handle"
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {records.length === 0 && !loading && !searchLoading ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-4 dme-empty-state border-0">
                                        No matching records found.
                                    </td>
                                </tr>
                            ) : (
                                records.map((row, idx) => {
                                    const badgeStyles = getSourceBadgeStyles(row.caseCreationSource);
                                    const patientNameFull = (row.firstName || row.lastName) ? `${row.firstName || ''} ${row.lastName || ''}`.trim() : '';
                                    return (
                                        <tr key={`${row.caseId || row.caseNo || idx}`} className={idx % 2 === 0 ? 'dme-row-even' : 'dme-row-odd'}>

                                            {/* 0 — Creation Source */}
                                            <td className="py-1 px-2" style={tdStyle(0)} title={row.caseCreationSource || ''}>
                                                {row.caseCreationSource ? (
                                                    <span className="badge fw-bold dme-px-15 dme-py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                        {row.caseCreationSource}
                                                    </span>
                                                ) : '—'}
                                            </td>

                                            {/* 1 — Company Name */}
                                            <td className="py-1 px-2 text-dark fw-medium" title={(row.companyName || '').toUpperCase()} style={{ ...tdStyle(1), textTransform: 'uppercase', fontSize: '11.5px' }}>
                                                {row.companyName || '—'}
                                            </td>

                                            {/* 2 — Case No */}
                                            <td className="py-1 px-2" title={row.caseNo || ''} style={tdStyle(2)}>
                                                {row.caseNo || '—'}
                                            </td>

                                            {/* 3 — Patient Name */}
                                            <td className="py-1 px-2" title={patientNameFull} style={tdStyle(3)}>
                                                {patientNameFull || '—'}
                                            </td>

                                            {/* 4 — Created Date */}
                                            <td className="text-secondary py-1 px-2" title={row.createdDate ? new Date(row.createdDate).toLocaleString() : ''} style={tdStyle(4)}>
                                                {row.createdDate ? new Date(row.createdDate).toLocaleString() : '—'}
                                            </td>

                                            {/* 5 — Updated Date */}
                                            <td className="text-secondary py-1 px-2" title={row.updatedDate ? new Date(row.updatedDate).toLocaleString() : ''} style={tdStyle(5)}>
                                                {row.updatedDate ? new Date(row.updatedDate).toLocaleString() : '—'}
                                            </td>

                                            {/* 6 — Created By */}
                                            <td className="py-1 px-2" title={row.createdByUser || ''} style={tdStyle(6)}>
                                                {row.createdByUser || '—'}
                                            </td>

                                            {/* 7 — Updated By */}
                                            <td className="py-1 px-2" title={row.updatedByUser || ''} style={tdStyle(7, true)}>
                                                {row.updatedByUser || '—'}
                                            </td>

                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                )}

                {viewMode === 'detailed' && (
                <div className="dme-mobile-cards">
                    {records.length === 0 && !loading && !searchLoading ? (
                        <div className="text-center py-4 text-muted">No matching records found.</div>
                    ) : (
                        records.map((row, idx) => {
                            const patientName = (row.firstName || row.lastName)
                                ? `${row.firstName || ''} ${row.lastName || ''}`.trim()
                                : 'Unnamed Patient';
                            const createdDateStr = row.createdDate ? new Date(row.createdDate).toLocaleString() : '—';
                            const updatedDateStr = row.updatedDate ? new Date(row.updatedDate).toLocaleString() : '—';
                            const isAI = (row.caseCreationSource || '').toUpperCase() === 'AI MODULE';
                            const badgeStyles = getSourceBadgeStyles(row.caseCreationSource);
                            return (
                                <div
                                    key={`card-${row.caseId || row.caseNo || idx}`}
                                    className={`dme-ecard ${isAI ? 'dme-ecard-ai' : 'dme-ecard-manual'}`}
                                >
                                    <div className="dme-ecard-top">
                                        <div className="dme-ecard-identity">
                                            <div className="dme-ecard-avatar" aria-hidden="true">
                                                {getInitials(row.firstName, row.lastName)}
                                            </div>
                                            <div className="dme-ecard-identity-text">
                                                <div className="dme-ecard-patient" title={patientName}>{patientName}</div>
                                                <div className="dme-ecard-company" title={(row.companyName || '').toUpperCase()}>
                                                    <Building2 size={11} className="dme-ecard-inline-icon" />
                                                    <span style={{ textTransform: 'uppercase' }}>{row.companyName || '—'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {row.caseCreationSource && (
                                            <span
                                                className="dme-ecard-badge"
                                                style={{ backgroundColor: badgeStyles.backgroundColor, color: badgeStyles.color }}
                                            >
                                                {row.caseCreationSource}
                                            </span>
                                        )}
                                    </div>

                                    <div className="dme-ecard-caseno">
                                        <Hash size={11} className="dme-ecard-inline-icon" />
                                        <span>{row.caseNo || '—'}</span>
                                    </div>

                                    <div className="dme-ecard-divider" />

                                    <div className="dme-ecard-meta-grid">
                                        <div className="dme-ecard-meta-item">
                                            <span className="dme-ecard-meta-label">
                                                <CalendarClock size={11} className="dme-ecard-inline-icon" />
                                                Created
                                            </span>
                                            <span className="dme-ecard-meta-value" title={createdDateStr}>{createdDateStr}</span>
                                            <span className="dme-ecard-meta-sub" title={row.createdByUser || ''}>
                                                by {row.createdByUser || '—'}
                                            </span>
                                        </div>
                                        <div className="dme-ecard-meta-item">
                                            <span className="dme-ecard-meta-label">
                                                <History size={11} className="dme-ecard-inline-icon" />
                                                Updated
                                            </span>
                                            <span className="dme-ecard-meta-value" title={updatedDateStr}>{updatedDateStr}</span>
                                            <span className="dme-ecard-meta-sub" title={row.updatedByUser || ''}>
                                                by {row.updatedByUser || '—'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                )}

                {viewMode === 'summary' && (
                    <div className="table-responsive dme-summary-table-wrap dme-summary-desktop-table" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                        {summaryLoading ? (
                            <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                                <RefreshCw size={14} style={{ animation: 'dme-spin 1s linear infinite' }} />
                                Loading summary...
                            </div>
                        ) : pivotRows.length === 0 ? (
                            <div className="text-center py-4 text-muted">No matching records found.</div>
                        ) : (
                            <table className="table table-bordered align-middle mb-0 dme-summary-table">
                                <thead className="sticky-top" style={{ zIndex: 10 }}>
                                    <tr>
                                        <th rowSpan={2} className="text-center align-middle dme-col-srno">Sr.no</th>
                                        <th rowSpan={2} className="align-middle dme-col-company">Company Name</th>
                                        <th colSpan={2} className="text-center dme-total-header">Total</th>
                                        {weekBuckets.map((w, i) => (
                                            <th key={w.label} colSpan={2} className={`text-center dme-week-header ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>{w.label}</th>
                                        ))}
                                    </tr>
                                    <tr>
                                        <th className="text-center dme-total-col-header dme-col-ai">AI Module</th>
                                        <th className="text-center dme-total-col-header dme-col-manual">Manual</th>
                                        {weekBuckets.map((w, i) => (
                                            <React.Fragment key={`${w.label}-sub`}>
                                                <th className={`text-center dme-week-divider dme-col-ai ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>AI Module</th>
                                                <th className={`text-center dme-col-manual ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>Manual</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pivotRows.map((row, idx) => (
                                        <tr key={row.companyName}>
                                            <td className="text-center dme-col-srno">{idx + 1}</td>
                                            <td className="text-dark fw-medium dme-col-company" title={(row.companyName || '').toUpperCase()} style={{ textTransform: 'uppercase', fontSize: '11px' }}>{row.companyName}</td>
                                            <td className="text-center dme-total-col dme-col-ai">{renderCount(row.total.ai)}</td>
                                            <td className="text-center dme-total-col dme-col-manual">{renderCount(row.total.manual)}</td>
                                            {weekBuckets.map((w, i) => (
                                                <React.Fragment key={`${row.companyName}-${w.label}`}>
                                                    <td className={`text-center dme-week-divider dme-col-ai ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>{renderCount(row.weekly[w.label].ai)}</td>
                                                    <td className={`text-center dme-col-manual ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>{renderCount(row.weekly[w.label].manual)}</td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="dme-grand-total-row">
                                        <td colSpan={2} className="dme-col-company dme-grand-total-label">Grand Total</td>
                                        <td className="text-center dme-total-col dme-col-ai">{grandTotal.total.ai}</td>
                                        <td className="text-center dme-total-col dme-col-manual">{grandTotal.total.manual}</td>
                                        {weekBuckets.map((w, i) => (
                                            <React.Fragment key={`gt-${w.label}`}>
                                                <td className={`text-center dme-week-divider dme-col-ai ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>{grandTotal.weekly[w.label]?.ai || 0}</td>
                                                <td className={`text-center dme-col-manual ${i % 2 === 0 ? 'dme-week-even' : 'dme-week-odd'}`}>{grandTotal.weekly[w.label]?.manual || 0}</td>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                )}

                {viewMode === 'summary' && (
                    <div className="dme-summary-mobile-cards">
                        {summaryLoading ? (
                            <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                                <RefreshCw size={14} style={{ animation: 'dme-spin 1s linear infinite' }} />
                                Loading summary...
                            </div>
                        ) : pivotRows.length === 0 ? (
                            <div className="text-center py-4 text-muted">No matching records found.</div>
                        ) : (
                            <>
                                <div className="dme-scard dme-scard-total">
                                    <div className="dme-scard-top">
                                        <div className="dme-scard-title">
                                            <span className="dme-scard-total-label">Grand Total</span>
                                            <span className="dme-scard-total-sub">{pivotRows.length} compan{pivotRows.length !== 1 ? 'ies' : 'y'}</span>
                                        </div>
                                    </div>
                                    <div className="dme-scard-stats">
                                        <div className="dme-scard-stat dme-scard-stat-ai">
                                            <span className="dme-scard-stat-value">{grandTotal.total.ai}</span>
                                            <span className="dme-scard-stat-label">AI Module</span>
                                        </div>
                                        <div className="dme-scard-stat dme-scard-stat-manual">
                                            <span className="dme-scard-stat-value">{grandTotal.total.manual}</span>
                                            <span className="dme-scard-stat-label">Manual</span>
                                        </div>
                                    </div>
                                </div>

                                {pivotRows.map((row, idx) => {
                                    const isExpanded = expandedCompanies.has(row.companyName);
                                    return (
                                        <div key={row.companyName} className="dme-scard">
                                            <button
                                                type="button"
                                                className="dme-scard-top dme-scard-toggle"
                                                onClick={() => toggleCompanyExpand(row.companyName)}
                                                aria-expanded={isExpanded}
                                            >
                                                <div className="dme-scard-title">
                                                    <span className="dme-scard-srno">{idx + 1}</span>
                                                    <span className="dme-scard-company" title={(row.companyName || '').toUpperCase()} style={{ textTransform: 'uppercase' }}>{row.companyName}</span>
                                                </div>
                                                <ChevronDown
                                                    size={16}
                                                    className={`dme-scard-chevron ${isExpanded ? 'dme-scard-chevron-open' : ''}`}
                                                />
                                            </button>

                                            <div className="dme-scard-stats">
                                                <div className="dme-scard-stat dme-scard-stat-ai">
                                                    <span className="dme-scard-stat-value">{renderCount(row.total.ai)}</span>
                                                    <span className="dme-scard-stat-label">AI Module</span>
                                                </div>
                                                <div className="dme-scard-stat dme-scard-stat-manual">
                                                    <span className="dme-scard-stat-value">{renderCount(row.total.manual)}</span>
                                                    <span className="dme-scard-stat-label">Manual</span>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="dme-scard-weeks">
                                                    {weekBuckets.map((w) => (
                                                        <div key={w.label} className="dme-scard-week-row">
                                                            <span className="dme-scard-week-label">{w.label}</span>
                                                            <span className="dme-scard-week-counts">
                                                                <span className="dme-scard-week-ai">{renderCount(row.weekly[w.label]?.ai || 0)} AI</span>
                                                                <span className="dme-scard-week-sep">·</span>
                                                                <span className="dme-scard-week-manual">{renderCount(row.weekly[w.label]?.manual || 0)} Manual</span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}

                {viewMode === 'detailed' && !loading && !searchLoading && records.length > 0 && (
                    <div
                        className="dme-pagination-bar"
                        style={{
                            padding: '10px 20px', borderTop: '1px solid #e5e7eb',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#fafafa', flexWrap: 'wrap', gap: '8px',
                        }}
                    >
                        <span className="dme-pagination-summary" style={{ fontSize: 12, color: '#6b7280' }}>
                            {totalCount === 0
                                ? 'No records'
                                : <>Showing {rangeStart}–{rangeEnd} of {totalCount}</>}
                        </span>

                        <div className="dme-pagination-controls" style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                            <button
                                onClick={() => goToPage(pageNumber - 1)}
                                disabled={pageNumber === 1 || loading}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${THEME.alpha(0.35)}`,
                                    background: pageNumber === 1 ? '#f3f4f6' : '#fff',
                                    color: pageNumber === 1 ? '#9ca3af' : THEME.primaryHover,
                                    cursor: pageNumber === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                                }}
                            >‹ Prev</button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => {
                                    if (totalPages <= 7) return true;
                                    if (p === 1 || p === totalPages) return true;
                                    if (p >= pageNumber - 2 && p <= pageNumber + 2) return true;
                                    return false;
                                })
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => goToPage(p)}
                                            disabled={loading}
                                            style={{
                                                width: 28, height: 28, borderRadius: 6, border: `1px solid ${THEME.alpha(0.35)}`,
                                                background: pageNumber === p ? THEME.primary : '#fff',
                                                color: pageNumber === p ? THEME.onPrimary : THEME.primaryHover,
                                                fontWeight: pageNumber === p ? 700 : 400,
                                                cursor: 'pointer', fontSize: 12
                                            }}
                                        >
                                            {loading && p === pageNumber
                                                ? <RefreshCw size={12} style={{ animation: 'dme-spin 1s linear infinite' }} />
                                                : p}
                                        </button>
                                    )
                                )
                            }

                            <button
                                onClick={() => goToPage(pageNumber + 1)}
                                disabled={pageNumber === totalPages || loading}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${THEME.alpha(0.35)}`,
                                    background: pageNumber === totalPages ? '#f3f4f6' : '#fff',
                                    color: pageNumber === totalPages ? '#9ca3af' : THEME.primaryHover,
                                    cursor: pageNumber === totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                                }}
                            >Next ›</button>
                        </div>

                        <div className="dme-pagesize-select" style={{ width: '110px' }}>
                            <Select
                                options={pageSizeSelectOptions}
                                value={pageSizeSelectOptions.find(o => o.value === pageSize)}
                                onChange={handlePageSizeChange}
                                isSearchable={false}
                                isDisabled={loading}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                classNamePrefix="react-select"
                            />
                        </div>
                    </div>
                )}
            </div>

            {viewMode === 'summary' && (
            <div className="card shadow-sm border-0 rounded-3 bg-white dme-card dme-trend-card">
                <div className="px-3 pt-3 pb-1 d-flex align-items-center justify-content-between dme-trend-header">
                    <div>
                        <div className="dme-trend-title">
                            Last {trendData.length || TREND_WEEKS_BACK} Week{(trendData.length || TREND_WEEKS_BACK) === 1 ? '' : 's'} Trend
                        </div>
                        <div className="dme-trend-subtitle">AI Module vs Manual case creation, by week</div>
                    </div>
                    {trendLoading && <RefreshCw size={14} style={{ animation: 'dme-spin 1s linear infinite', color: '#6c757d' }} />}
                </div>
                <div className="px-2 pb-3 dme-trend-chart-area" style={{ height: 320 }}>
                    {trendLoading ? (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 12.5 }}>
                            Loading trend...
                        </div>
                    ) : trendData.length === 0 ? (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 12.5 }}>
                            No data available.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={trendData}
                                margin={isMobile ? { top: 8, right: 4, left: -20, bottom: 0 } : { top: 8, right: 16, left: 0, bottom: 0 }}
                                barGap={0}
                                barCategoryGap={isMobile ? '12%' : '20%'}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f0" />
                                <XAxis
                                    dataKey={isMobile ? 'shortLabel' : 'label'}
                                    tick={{ fontSize: isMobile ? 10.5 : 12, fill: '#6c757d' }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={false}
                                    interval={0}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    width={isMobile ? 28 : 36}
                                    tick={{ fontSize: isMobile ? 10.5 : 12, fill: '#6c757d' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    labelFormatter={(value, payload) => (payload && payload[0] ? payload[0].payload.label : value)}
                                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                                    labelStyle={{ fontWeight: 600, color: '#33403c' }}
                                />
                                <Legend
                                    wrapperStyle={{ fontSize: isMobile ? 11 : 12 }}
                                    iconType="circle"
                                    iconSize={isMobile ? 8 : 10}
                                />
                                <Bar dataKey="ai" name="AI Module" fill={chartPrimary} radius={[4, 4, 0, 0]} maxBarSize={isMobile ? 22 : 40} />
                                <Bar dataKey="manual" name="Manual" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={isMobile ? 22 : 40} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

export default DMEDataEntryReport;